import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Client sub-roles:
 * - client_admin: Full access + manage company users
 * - client_finance: Access to financial data (invoices, quotes, payments)
 * - client_logistique: Operations, tracking, NO financial data
 * - client_auditor: Read-only access to everything
 * - null/undefined: Full access (backward compatible, same as client_admin)
 */

const CLIENT_ROLE_LABELS = {
  client_admin: 'Administrateur',
  client_finance: 'Finance',
  client_logistique: 'Logistique',
  client_auditor: 'Auditeur',
};

export const getClientRoleLabel = (role) => CLIENT_ROLE_LABELS[role] || 'Complet';

export const useClientPermissions = () => {
  const { user } = useAuth();

  return useMemo(() => {
    const clientRole = user?.client_role;
    const isClient = user?.role === 'client' || user?.role === 'expediteur';

    // Non-client users or clients without sub-role have full access
    if (!isClient || !clientRole) {
      return {
        clientRole: null,
        canViewFinances: true,
        canViewOperations: true,
        canCreateOperations: true,
        canEditOperations: true,
        canViewContracts: true,
        canViewTracking: true,
        canViewMessaging: true,
        canViewIncidents: true,
        canViewReports: true,
        canManageUsers: false,
        isReadOnly: false,
        canViewPrices: true,
        roleLabel: getClientRoleLabel(clientRole),
      };
    }

    const isAuditor = clientRole === 'client_auditor';
    const isLogistique = clientRole === 'client_logistique';
    const isFinance = clientRole === 'client_finance';
    const isAdmin = clientRole === 'client_admin';

    return {
      clientRole,
      // Financial access: denied for client_logistique
      canViewFinances: !isLogistique,
      canViewPrices: !isLogistique,
      // Operations
      canViewOperations: true,
      canCreateOperations: !isAuditor,
      canEditOperations: !isAuditor,
      // Contracts
      canViewContracts: true,
      // Tracking
      canViewTracking: true,
      // Messaging
      canViewMessaging: !isAuditor,
      // Incidents
      canViewIncidents: true,
      // Reports
      canViewReports: true,
      // User management: only client_admin
      canManageUsers: isAdmin,
      // Read-only: auditor
      isReadOnly: isAuditor,
      roleLabel: getClientRoleLabel(clientRole),
    };
  }, [user?.client_role, user?.role]);
};
